import React, { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router';
import { StylishNewToggleButtonGroup } from 'components/DesignSystems/New/StylishNewToggleButtonGroup';
import {
  GetSitrepsResponse,
  SitrepViewDataProps,
  SitrepViewTabs,
} from 'components/Reports/reports.types';
import CurrentReportView from 'components/Reports/CurrentReportView';
import DraftReportView from 'components/Reports/DraftReportView';
import HistoricalReportView from 'components/Reports/HistoricalReportView';
import ReviewReportView from 'components/Reports/ReviewReportView';
import { Incident } from 'components/IAP/hooks/incidentHooks';
import { AreaOfResponsibility } from 'types/aor';

const getAorIdFromIncident = (
  incident: Incident,
  aors: AreaOfResponsibility[]
) => {
  const incidentAors = aors.filter((aor) =>
    incident.areas_of_responsibility.includes(aor.id)
  );

  return incidentAors[0]?.id ?? null;
};

type ReportSitrepViewProps = {
  scopeName: string;
  publishedSitreps: SitrepViewDataProps;
  draftSitreps: SitrepViewDataProps;
  pendingReviews: SitrepViewDataProps;
  aors: AreaOfResponsibility[];
  aorId?: string;
  incident?: Incident;
};

const getTabDisplayName = (tab: SitrepViewTabs) => {
  switch (tab) {
    case SitrepViewTabs.CurrentSitrep:
      return 'Current';
    case SitrepViewTabs.Historical:
      return 'Historical';
    case SitrepViewTabs.Drafts:
      return 'Drafts';
    case SitrepViewTabs.Reviews:
      return 'Pending Reviews';
    default:
      return '';
  }
};

const ReportsSitrepView: React.FC<ReportSitrepViewProps> = ({
  scopeName,
  publishedSitreps,
  draftSitreps,
  pendingReviews,
  aors,
  aorId,
  incident,
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const currentTab = useMemo(() => {
    return (
      (searchParams.get('sitrepTab') as SitrepViewTabs) ||
      SitrepViewTabs.CurrentSitrep
    );
  }, [searchParams]);
  const setCurrentTab = useCallback(
    (newTab: SitrepViewTabs) => {
      setSearchParams(
        (searchParams) => {
          searchParams.set('sitrepTab', newTab);
          return searchParams;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const latestSitreps: Map<string, GetSitrepsResponse> = useMemo(() => {
    return publishedSitreps.data.reduce((latestReps, currentRep) => {
      const relevantId = currentRep.incident_id || currentRep.aor_id;
      if (
        new Date(currentRep.timestamp).getTime() >
        (new Date(latestReps.get(relevantId)?.timestamp).getTime() || 0)
      ) {
        latestReps.set(relevantId, currentRep);
      }
      return latestReps;
    }, new Map());
  }, [publishedSitreps]);

  const historicalSitreps = useMemo(() => {
    return publishedSitreps.data
      .filter((sitrep) => {
        if (sitrep.incident_id) {
          return sitrep.id !== latestSitreps.get(sitrep.incident_id)?.id;
        } else if (sitrep.aor_id) {
          return sitrep.id !== latestSitreps.get(sitrep.aor_id)?.id;
        }
        return false;
      })
      .sort(
        (a, b) =>
          new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
      );
  }, [publishedSitreps, latestSitreps]);

  const derivedAorId = useMemo(() => {
    if (aorId) {
      return aorId;
    } else if (incident) {
      return getAorIdFromIncident(incident, aors);
    }

    console.error(
      'No AOR ID found matching the provided incident. Unable to create draft SITREP with correct AOR association.'
    );
    return undefined;
  }, [incident, aors, aorId]);

  const getTabDisplay = useMemo(() => {
    switch (currentTab) {
      case SitrepViewTabs.CurrentSitrep:
        return (
          <CurrentReportView
            scopeName={scopeName}
            latestSitreps={latestSitreps}
            isLoading={publishedSitreps.isLoading}
            isError={publishedSitreps.isError}
            derivedAorId={derivedAorId}
            incident={incident}
          />
        );
      case SitrepViewTabs.Historical:
        return (
          <HistoricalReportView
            historicalSitreps={historicalSitreps}
            isLoading={publishedSitreps.isLoading}
            isError={publishedSitreps.isError}
          />
        );
      case SitrepViewTabs.Drafts:
        return (
          <DraftReportView
            draftSitreps={draftSitreps}
            scopeName={scopeName}
            derivedAorId={derivedAorId}
            incident={incident}
          />
        );
      case SitrepViewTabs.Reviews:
        return <ReviewReportView pendingReviews={pendingReviews} />;
      default:
        return null;
    }
  }, [
    currentTab,
    scopeName,
    latestSitreps,
    historicalSitreps,
    draftSitreps,
    pendingReviews,
    publishedSitreps.isLoading,
    publishedSitreps.isError,
    derivedAorId,
    incident,
  ]);

  return (
    <>
      <StylishNewToggleButtonGroup
        className="reports-sitrep-tab-selector"
        size="sml"
        data={Object.values(SitrepViewTabs).map((tab) => ({
          label: getTabDisplayName(tab),
          value: tab,
        }))}
        selected={currentTab}
        onChange={(value) => setCurrentTab(value as SitrepViewTabs)}
      />
      {getTabDisplay}
    </>
  );
};

export default ReportsSitrepView;
